import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const usd = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toFixed(2);

/**
 * Marketplace → "From suppliers": the bot's linked suppliers' products, and
 * which of them resellers can buy on the website (in Gold Coins). Nothing is
 * on the website until it is switched on here. Orders are bought from the
 * supplier automatically; a failed one is refunded in Gold Coins.
 */
function MarketplaceSupplierProducts() {
    const [rows, setRows] = useState([]);
    const [rate, setRate] = useState(1);
    const [loaded, setLoaded] = useState(false);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');   // all | on | off
    const [draft, setDraft] = useState({});
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [shown, setShown] = useState(60);
    const [links, setLinks] = useState([]);
    const [linkFor, setLinkFor] = useState(null);      // supplier product being linked
    const [options, setOptions] = useState([]);        // own product+duration choices
    const [optSearch, setOptSearch] = useState('');

    const authFetch = useCallback(async (path, options = {}) => {
        try {
            const res = await fetch(`${API_URL}${path}`, {
                ...options,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}`, ...(options.headers || {}) },
            });
            return { ok: res.ok, body: await res.json().catch(() => ({})) };
        } catch (e) {
            return { ok: false, body: { error: 'Could not reach the server — try again.' } };
        }
    }, []);

    const load = useCallback(async () => {
        const [{ ok, body }, l] = await Promise.all([
            authFetch('/admin/marketplace/supplier-products'),
            authFetch('/admin/marketplace/supplier-links')
        ]);
        if (ok) { setRows(body.products || []); setRate(body.usdPerGc || 1); }
        if (l.ok) setLinks(l.body.links || []);
        setLoaded(true);
    }, [authFetch]);

    const openLink = async (r) => {
        setLinkFor(r);
        setOptSearch((r.name.split(/\s+/)[0]) || '');
        if (!options.length) {
            const { ok, body } = await authFetch('/admin/marketplace/link-options');
            if (ok) setOptions(body.options || []);
        }
    };

    const saveLink = async (productId, durationMonths, supplierBotId) => {
        const { ok, body } = await authFetch('/admin/marketplace/supplier-links', { method: 'PUT', body: JSON.stringify({ productId, durationMonths, supplierBotId }) });
        if (!ok) { flash(body.error || 'Could not save the link.', true); return; }
        flash(supplierBotId ? 'Linked: when your own codes run out, sales are bought from the supplier.' : 'Link removed.');
        setLinkFor(null);
        load();
    };
    useEffect(() => { load(); }, [load]);

    const flash = (text, bad = false) => { setMsg({ text, bad }); setTimeout(() => setMsg(null), 6000); };

    const save = async (p, patch) => {
        const { ok, body } = await authFetch(`/admin/marketplace/supplier-products/${p.id}`, { method: 'PUT', body: JSON.stringify(patch) });
        if (!ok) { flash(body.error || 'Could not save.', true); return false; }
        setRows(rs => rs.map(r => r.id === p.id ? body.product : r));
        return true;
    };

    const commitPrice = async (p) => {
        const raw = draft[p.id];
        if (raw === undefined) return;
        const v = Number(raw);
        const clear = () => setDraft(d => { const n = { ...d }; delete n[p.id]; return n; });
        if (!(v > 0)) { flash('Enter a Gold Coin price above zero.', true); return; }
        if (v * rate <= p.costUsd && !window.confirm(`${v} GC is not above the supplier cost (${usd(p.costUsd)}) — it will NOT be sold at this price. Save anyway?`)) return;
        if (await save(p, { webPriceGc: v })) { clear(); flash(`${p.name}: ${v} GC`); }
    };

    const toggle = async (p) => {
        if (!p.webListed) {
            const price = draft[p.id] !== undefined ? Number(draft[p.id]) : (p.webPriceGc || p.suggestedGc);
            if (!(price > 0)) { flash('Set a Gold Coin price first.', true); return; }
            if (await save(p, { webListed: true, webPriceGc: price })) flash(`${p.name} is now on the website at ${price} GC.`);
        } else if (await save(p, { webListed: false })) flash(`${p.name} taken off the website.`);
    };

    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const visible = rows
        .filter(r => !r.deleted && !r.gone)
        .filter(r => filter === 'all' || (filter === 'on' ? r.webListed : !r.webListed))
        .filter(r => words.every(w => `${r.name} ${r.supplierName}`.toLowerCase().includes(w)));

    const bulk = async (on) => {
        const ids = visible.filter(r => r.webListed !== on && (!on || (r.instant && r.inStock))).map(r => r.id);
        if (!ids.length) { flash(on ? 'Nothing in stock to add in this view.' : 'Nothing to take off in this view.', true); return; }
        if (!window.confirm(on
            ? `Put ${ids.length} in-stock product(s) on the website? Products without a Gold Coin price get the bot's price converted (${rate} USD = 1 GC).`
            : `Take ${ids.length} product(s) off the website?`)) return;
        setBusy(true);
        const { ok, body } = await authFetch('/admin/marketplace/supplier-products/bulk', { method: 'POST', body: JSON.stringify({ ids, webListed: on }) });
        setBusy(false);
        if (!ok) { flash(body.error || 'Failed.', true); return; }
        flash(`${body.changed} updated${body.skipped && body.skipped.length ? ` · ${body.skipped.length} skipped (no price)` : ''}.`);
        load();
    };

    if (!loaded) return <div style={{ padding: 20, color: '#64748b' }}>Loading supplier products…</div>;
    if (!rows.length) return (
        <div style={{ padding: 20, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', color: '#475569' }}>
            No supplier linked yet. Link one in <b>Telegram Shop → Suppliers</b> (ProdSeller, HyleHub, InsightXPro, Jero) — its products then show up here.
        </div>
    );

    const onCount = rows.filter(r => r.webListed && !r.deleted && !r.gone).length;

    return (
        <div style={{ padding: 20, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <h4 style={{ marginTop: 0, marginBottom: 4 }}>Products from suppliers</h4>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.6 }}>
                Your linked suppliers' products. Switch on the ones resellers can buy here, and set their Gold Coin price.
                When a reseller buys, it is bought from the supplier with your balance there and the code appears in their orders within seconds;
                if the supplier can't deliver, the reseller is refunded automatically. Stock and cost update every 15 minutes.
                A price at or below the supplier cost is never sold. <b>{onCount}</b> on the website now.
            </p>
            {msg && <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13, background: msg.bad ? '#fef2f2' : '#f0fdf4', color: msg.bad ? '#991b1b' : '#166534', border: `1px solid ${msg.bad ? '#fecaca' : '#bbf7d0'}` }}>{msg.text}</div>}
            {links.length > 0 && (
                <div style={{ marginBottom: 14, padding: 12, borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <b style={{ fontSize: 13 }}>🔗 Your products restocked from suppliers ({links.length})</b>
                    <div style={{ fontSize: 12, color: '#475569', margin: '2px 0 8px' }}>Your own codes sell first; when they run out, each sale is bought from the supplier (website and bot).</div>
                    <table className="data-table">
                        <thead><tr><th>Your product</th><th>Price</th><th>Supplier product</th><th>Cost</th><th>Supplier stock</th><th></th></tr></thead>
                        <tbody>
                            {links.map(l => (
                                <tr key={l.productId + ':' + l.durationMonths}>
                                    <td style={{ fontWeight: 600 }}>{l.productName} <span style={{ fontWeight: 400, color: '#64748b' }}>· {l.durationLabel}</span></td>
                                    <td>{l.priceGc != null ? l.priceGc + ' GC' : '—'}{l.belowCost && <div style={{ fontSize: 11, color: '#dc2626' }}>at/below cost — supplier not used</div>}</td>
                                    <td>{l.supplierProduct} <span style={{ color: '#64748b', fontSize: 12 }}>({l.supplierName || 'supplier'})</span></td>
                                    <td>{usd(l.costUsd)}</td>
                                    <td>{l.supplierStock > 0 ? l.supplierStock : <span style={{ color: '#dc2626' }}>0</span>}</td>
                                    <td><button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: 12 }}
                                        onClick={() => { if (window.confirm('Stop restocking "' + l.productName + ' · ' + l.durationLabel + '" from the supplier?')) saveLink(l.productId, l.durationMonths, null); }}>Unlink</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
                <input className="form-input" style={{ maxWidth: 260 }} placeholder="Search name or supplier…" value={search} onChange={e => { setSearch(e.target.value); setShown(60); }} />
                <select className="form-input" style={{ width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
                    <option value="all">All ({rows.filter(r => !r.deleted && !r.gone).length})</option>
                    <option value="on">On website ({onCount})</option>
                    <option value="off">Not on website</option>
                </select>
                <button className="btn btn-primary" disabled={busy} onClick={() => bulk(true)}>Put all in-stock shown on website</button>
                <button className="btn btn-secondary" disabled={busy} onClick={() => bulk(false)}>Take all shown off</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                    <thead>
                        <tr><th>Product</th><th>Supplier</th><th>Duration</th><th>Cost</th><th>Bot price</th><th>Website price (GC)</th><th>Stock</th><th>On website</th><th>Restocks</th></tr>
                    </thead>
                    <tbody>
                        {visible.slice(0, shown).map(r => {
                            const typed = draft[r.id];
                            const value = typed !== undefined ? typed : (r.webPriceGc ?? '');
                            const price = Number(value);
                            const loss = price > 0 && price * rate <= r.costUsd;
                            const delayed = !r.instant;
                            return (
                                <tr key={r.id} style={{ opacity: r.supplierEnabled ? 1 : 0.55 }}>
                                    <td style={{ minWidth: 220, fontWeight: 600 }}>
                                        {r.name}
                                        {delayed && <span style={{ marginLeft: 6, fontSize: 11, color: '#b45309' }}>(delivered later)</span>}
                                        {r.requiresEmail && <span style={{ marginLeft: 6, fontSize: 11, color: '#2563eb' }}>(needs buyer email)</span>}
                                    </td>
                                    <td>{r.supplierName}{!r.supplierEnabled && <span style={{ fontSize: 11, color: '#64748b' }}> (paused)</span>}</td>
                                    <td>{r.monthsLabel}</td>
                                    <td>{usd(r.costUsd)}</td>
                                    <td>{usd(r.botSellUsd)}</td>
                                    <td>
                                        <input className="form-input" style={{ width: 96, borderColor: loss ? '#dc2626' : undefined }} type="number" min="0" step="0.01"
                                            placeholder={r.suggestedGc ? String(r.suggestedGc) : 'GC'}
                                            value={value}
                                            onChange={e => setDraft(d => ({ ...d, [r.id]: e.target.value }))}
                                            onBlur={() => r.webListed || r.webPriceGc ? commitPrice(r) : null}
                                            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                                        {loss && <div style={{ fontSize: 11, color: '#dc2626' }}>below cost — not sold</div>}
                                    </td>
                                    <td>{!r.inStock ? <span style={{ color: '#dc2626' }}>0</span> : (r.stock > 0 ? r.stock : '✓')}</td>
                                    <td>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', margin: 0 }}>
                                            <input type="checkbox" checked={!!r.webListed} onChange={() => toggle(r)} style={{ width: 18, height: 18 }} />
                                            <span style={{ fontSize: 12, color: r.webListed ? '#15803d' : '#64748b' }}>{r.webListed ? 'On' : 'Off'}</span>
                                        </label>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {r.instant && !r.requiresEmail ? (() => {
                                            const n = links.filter(l => l.supplierBotId === r.botId).length;
                                            return (
                                                <button className="btn btn-secondary" style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => openLink(r)}
                                                    title="Use this as the source for one of your own products when its codes run out">
                                                    🔗 {n ? 'My products (' + n + ')' : 'Restock my product'}
                                                </button>
                                            );
                                        })() : <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {linkFor && (
                <div onClick={() => setLinkFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div onClick={e => e.stopPropagation()} className="card" style={{ borderRadius: 12, padding: 20, width: 'min(640px, 100%)', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', margin: 0 }}>
                        <h3 style={{ marginTop: 0 }}>🔗 Restock one of your products</h3>
                        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
                            Source: <b>{linkFor.name}</b> ({linkFor.supplierName}, cost {usd(linkFor.costUsd)}).
                            Pick YOUR product and duration that sells the same thing. Your own codes still sell first; after that each sale is bought here.
                            A price at or below this cost never uses the supplier.
                        </p>
                        <input className="form-input" autoFocus placeholder="Search your products…" value={optSearch} onChange={e => setOptSearch(e.target.value)} style={{ marginBottom: 10 }} />
                        <div style={{ display: 'grid', gap: 6 }}>
                            {options.filter(o => optSearch.trim().toLowerCase().split(/\s+/).filter(Boolean).every(w => o.name.toLowerCase().includes(w))).slice(0, 60).map(o => {
                                const current = links.find(l => l.productId === o.productId && l.durationMonths === o.durationMonths);
                                const loss = o.priceGc != null && Number(o.priceGc) * rate <= Number(linkFor.costUsd);
                                return (
                                    <button key={o.productId + ':' + o.durationMonths} type="button" className="btn btn-secondary"
                                        style={{ textAlign: 'left', display: 'flex', justifyContent: 'space-between', gap: 8, opacity: o.active ? 1 : 0.6 }}
                                        onClick={() => {
                                            if (current && current.supplierBotId !== linkFor.botId && !window.confirm('"' + o.name + ' · ' + o.durationLabel + '" is already restocked from "' + current.supplierProduct + '". Replace it?')) return;
                                            saveLink(o.productId, o.durationMonths, linkFor.botId);
                                        }}>
                                        <span>{o.name} <span style={{ color: '#64748b' }}>· {o.durationLabel}</span>{!o.active && ' (disabled)'}</span>
                                        <span style={{ fontSize: 12, color: loss ? '#dc2626' : '#64748b' }}>
                                            {o.priceGc != null ? o.priceGc + ' GC' : 'no price'}{loss ? ' · below cost' : ''}{current ? (current.supplierBotId === linkFor.botId ? ' · ✓ linked' : ' · linked elsewhere') : ''}
                                        </span>
                                    </button>
                                );
                            })}
                            {!options.length && <div style={{ color: '#64748b', fontSize: 13 }}>Loading your products…</div>}
                        </div>
                        <div style={{ textAlign: 'right', marginTop: 12 }}>
                            <button className="btn btn-secondary" onClick={() => setLinkFor(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {visible.length > shown && (
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <button className="btn btn-secondary" onClick={() => setShown(n => n + 60)}>Show more ({visible.length - shown} left)</button>
                </div>
            )}
        </div>
    );
}

export default MarketplaceSupplierProducts;
